// ターン単位の計測 artifact (`turns/*/metrics.json`) を、rolling buffer から組み立てる。
//
// **なぜ要るか** (docs/next-pilot-readiness.md A4): この artifact は 2026-08-05 の
// パイロットで **56 件すべて空** だった (R2 上のサイズが全件 107 バイトで固定 =
// 骨組みだけ)。`MetricsArtifactSchema` は `stt` / `llm` / `tts` を持っているのに、
// 呼び出し側が `schemaVersion` / `turnId` / `recordedAt` の 3 つしか渡していなかった。
// STT だけでなく **LLM と TTS のレイテンシも同じ理由で 1 件も残っていない**
// (それらは計装自体は生きており、turn 単位の artifact に載せていないだけ)。
//
// 純関数として切り出してあるのは、`getMetrics()` (localStorage) と React を
// 巻き込まずに窓の切り方と写し方だけを検証できるようにするため。

import type { Metric } from "./metrics";
import type { MetricsArtifact } from "../storage/types";

// 16 kHz mono の PCM16 = 1 秒あたり 32,000 バイト。`bytesUploaded` は
// 実際に送った WS フレームの合計ではなく、音声の長さからの換算値。
const BYTES_PER_MS = (16000 * 2) / 1000;

type TurnMetrics = Pick<MetricsArtifact, "stt" | "llm" | "tts">;

/**
 * `sinceMs` 以降にこの session で記録された metric から、ターン 1 つ分を組み立てる。
 *
 * **同じ種類が複数あれば最後のものを採る** — 言い直したターンでは STT も LLM も
 * 複数回走るので、実際に回答になった試行 (最後) を代表値にする。合計にしないのは
 * レイテンシを足しても意味が無いため。**費用の正は `summary.json` 側**で、
 * あちらは窓を切らず全件を集計するので、ここで取りこぼしても総額は狂わない。
 */
export function buildTurnMetrics(
  metrics: Metric[],
  sessionId: string,
  sinceMs: number
): TurnMetrics {
  // 窓の外と別 session を落とす。**session で絞るのを忘れると**、rolling buffer は
  // 直近 200 件を session をまたいで持つので turn artifact が別の人の計測を持つ。
  const inTurn = metrics.filter((m) => m.recordedAt >= sinceMs && m.sessionId === sessionId);
  // 後ろから探すのが「最後のものを採る」の実体。kind ごとに書くのは、
  // generic の型引数では `m.kind === kind` が union を絞ってくれないため。
  const rev = [...inTurn].reverse();
  // STT は「**この窓の中で最後に始まった発話の頭**」を採る = `audioDurationMs` を持つ
  // 最後のもの。単に最後を採ると、割れた確定文の 2 通目以降 (レイテンシも音声長も
  // 持たない) が選ばれて `latencyMs: 0 / bytesUploaded: 0 / costUsd: 0` になる
  // (preview 実機で実測、2026-08-18)。まさに直したかった「0ms / 0 円」が別の形で戻る。
  // 言い直したターンでは最後の試行の頭になるので、「最後の試行を採る」も同時に満たす。
  const s = rev.find(
    (m): m is Extract<Metric, { kind: "stt" }> => m.kind === "stt" && m.audioDurationMs !== null
  );
  const l = rev.find((m): m is Extract<Metric, { kind: "structure" }> => m.kind === "structure");
  const t = rev.find((m): m is Extract<Metric, { kind: "tts" }> => m.kind === "tts");

  return {
    ...(s
      ? {
          stt: {
            latencyMs: s.roundTripMs,
            // 上で head だけを選んでいるので `audioDurationMs` は非 null。
            // `?? 0` は型を閉じるためのもので、到達する経路は無い。
            bytesUploaded: Math.round((s.audioDurationMs ?? 0) * BYTES_PER_MS),
            costUsd: s.estimatedCostUsd,
          },
        }
      : {}),
    // schema 側の名前は `llm`、rolling buffer 側の kind は `structure`。
    // 同じものを指すので、ここが両者の唯一の対応点になる。
    ...(l
      ? {
          llm: {
            latencyMs: l.roundTripMs,
            promptTokens: l.inputTokens,
            completionTokens: l.outputTokens,
            costUsd: l.estimatedCostUsd,
          },
        }
      : {}),
    ...(t
      ? { tts: { latencyMs: t.roundTripMs, characters: t.chars, costUsd: t.estimatedCostUsd } }
      : {}),
  };
}
